/**
 * Local notifications — what the phone should remind the user of, planned
 * whole on every call and scheduled on the device. Nothing is pushed from a
 * server; there is no server-side scheduler to push from.
 *
 * Two kinds, both plain working voice (D-7: a notification is a control, not a
 * place for the myth):
 *
 * - **A repeating expense is due** — at 09:00 in the profile's timezone, on
 *   each of a rule's next few due dates. It asks the user to confirm; it never
 *   implies the expense was added, because it was not (SPEC §6 — a rule never
 *   inserts on the user's behalf). Due dates are the rule's own:
 *   `currentDueDate()` from `src/domain/recurring.ts`, stepped month by month
 *   with the same clamping of the 29th–31st.
 * - **The trial ends in two days.**
 *
 * Capped at 60 — iOS keeps at most 64 pending — with the trial warning kept
 * first when the cap bites, since it is the one that cannot be re-derived from
 * the ledger later.
 */

import { formatMoney } from "@/domain/money";
import { type YMD, clampedStartDay, parseYmd, ymdToIso, zonedMidnight } from "@/domain/period";
import { type RecurringLike, currentDueDate } from "@/domain/recurring";

import type { PlannedNotification } from "./bridge";

export const MAX_SCHEDULED = 60;
/** Upcoming due dates scheduled per rule; the list is replanned on every open. */
export const RECURRING_OCCURRENCES = 3;
export const REMINDER_HOUR = 9;
export const TRIAL_WARNING_DAYS = 2;

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
const MONTHS_PER_YEAR = 12;
/** Enough months to find RECURRING_OCCURRENCES dates even if this month's has passed. */
const MONTHS_SCANNED = RECURRING_OCCURRENCES + 1;

export interface NotificationRule extends RecurringLike {
  readonly deletedAt: string | null;
}

export interface NotificationProfile {
  readonly timezone: string;
  readonly currency: string;
}

export interface NotificationInput {
  readonly rules: readonly NotificationRule[];
  readonly profile: NotificationProfile;
  /** When the free trial ends, or null when there is none. */
  readonly trialEndsAt: string | null;
  readonly now: Date;
}

const hourFormatters = new Map<string, Intl.DateTimeFormat>();

function localHour(instant: Date, timeZone: string): number {
  let fmt = hourFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", { timeZone, hour: "2-digit", hourCycle: "h23" });
    hourFormatters.set(timeZone, fmt);
  }
  return Number(fmt.format(instant));
}

/**
 * 09:00 local on a calendar date. Midnight plus nine hours is an hour out on
 * the morning a zone changes its clocks, so the guess is corrected against the
 * zone's own reading of the hour.
 */
function reminderTime(ymd: YMD, timeZone: string): Date {
  let at = zonedMidnight(ymd, timeZone).getTime() + REMINDER_HOUR * MS_PER_HOUR;
  const drift = localHour(new Date(at), timeZone) - REMINDER_HOUR;
  at -= drift * MS_PER_HOUR;
  return new Date(at);
}

function dueDatesFrom(rule: NotificationRule, now: Date, timeZone: string): YMD[] {
  const anchor = parseYmd(currentDueDate(rule.dayOfMonth, now, timeZone));
  if (anchor === null) throw new Error(`Rule ${rule.id} produced an unreadable due date`);
  const dates: YMD[] = [];
  for (let k = 0; k <= MONTHS_SCANNED; k++) {
    const index = anchor.year * MONTHS_PER_YEAR + (anchor.month - 1) + k;
    const year = Math.floor(index / MONTHS_PER_YEAR);
    const month = (index % MONTHS_PER_YEAR) + 1;
    dates.push({ year, month, day: clampedStartDay(year, month, rule.dayOfMonth) });
  }
  return dates;
}

function recurringFor(rule: NotificationRule, profile: NotificationProfile, now: Date): PlannedNotification[] {
  const planned: PlannedNotification[] = [];
  for (const due of dueDatesFrom(rule, now, profile.timezone)) {
    const iso = ymdToIso(due);
    if (rule.lastMaterialisedOn !== null && iso <= rule.lastMaterialisedOn) continue;
    const at = reminderTime(due, profile.timezone);
    if (at.getTime() <= now.getTime()) continue;
    const note = rule.note?.trim();
    planned.push({
      id: `recurring_due:${rule.id}:${iso}`,
      at: at.toISOString(),
      kind: "recurring_due",
      title: note ? `${note} is due today` : "A repeating expense is due today",
      body: `${formatMoney(rule.amountCents, profile.currency)} · confirm it to add it to your ledger.`,
    });
    if (planned.length === RECURRING_OCCURRENCES) break;
  }
  return planned;
}

function trialWarning(trialEndsAt: string, profile: NotificationProfile, now: Date): PlannedNotification | null {
  const ends = new Date(trialEndsAt);
  if (!Number.isFinite(ends.getTime())) throw new Error(`Unreadable trial end: "${trialEndsAt}"`);
  const at = new Date(ends.getTime() - TRIAL_WARNING_DAYS * MS_PER_DAY);
  if (at.getTime() <= now.getTime()) return null;
  const day = new Intl.DateTimeFormat("en-SG", {
    timeZone: profile.timezone,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(ends);
  return {
    id: `trial_ending:${ends.toISOString()}`,
    at: at.toISOString(),
    kind: "trial_ending",
    title: `Your free trial ends in ${String(TRIAL_WARNING_DAYS)} days`,
    body: `It ends on ${day}. Choose a plan in Settings › Plan to keep logging.`,
  };
}

export function planNotifications(input: NotificationInput): PlannedNotification[] {
  const { profile, now } = input;
  const trial = input.trialEndsAt === null ? null : trialWarning(input.trialEndsAt, profile, now);

  const recurring = input.rules
    .filter((r) => r.active && r.deletedAt === null)
    .flatMap((r) => recurringFor(r, profile, now))
    .sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));

  const kept = [...(trial ? [trial] : []), ...recurring].slice(0, MAX_SCHEDULED);
  return kept.sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
}

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const OS_ID_MASK = 0x7fffffff;

/** FNV-1a folded into a positive signed 31-bit OS identifier. */
export function notificationId(value: string): number {
  let hash = FNV_OFFSET;
  for (let i = 0; i < value.length; i++) hash = Math.imul(hash ^ value.charCodeAt(i), FNV_PRIME);
  return (hash >>> 0) & OS_ID_MASK;
}

/** Resolve rare hash collisions deterministically rather than replacing another reminder. */
export function notificationRows(plan: readonly PlannedNotification[]) {
  const used = new Set<number>();
  return [...plan].slice(0, MAX_SCHEDULED).sort((a, b) => a.id.localeCompare(b.id)).map((row) => {
    let id = notificationId(row.id);
    while (used.has(id)) id = (id + 1) & OS_ID_MASK;
    used.add(id);
    return { id, title: row.title, body: row.body,
      schedule: { at: new Date(row.at), allowWhileIdle: true }, extra: { kind: row.kind } };
  });
}
